import type {
  CellClickedEvent,
  CellDoubleClickedEvent,
  CellValueChangedEvent,
  GridApi,
  GridReadyEvent,
  RowClassRules,
  RowSelectedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useAppStore } from '../store.js';
import { EDIT_YES } from '@query/shared';
import type { GridRow } from '../lib/columns.js';

/** Цветовые классы строк — значения перенесены из querys.css fullWeb. */
const ROW_CLASS_RULES: RowClassRules<GridRow> = {
  liteGray: (params) => (params.rowIndex ?? 0) % 2 === 0,
  cl1: (params) => params.data?.status === 'admin',
  cl2: (params) => params.data?.status === 'foodmaker',
  cl3: (params) => params.data?.status === 'feedmaker',
};

/**
 * Таблица — перенос rowsGreed (AppFC.kt:447-501).
 *
 * Обработчики читают состояние из store напрямую: в Kotlin-версии они были замыканиями
 * над глобальными переменными, и такое устройство сохраняется.
 */
export function DataGrid() {
  const rowList = useAppStore((s) => s.rowList);
  const colList = useAppStore((s) => s.colList);
  const editIcon = useAppStore((s) => s.editIcon);
  const setGridApi = useAppStore((s) => s.setGridApi);
  const setCellValue = useAppStore((s) => s.setCellValue);
  const deleteSelectedRows = useAppStore((s) => s.deleteSelectedRows);

  const handleGridReady = (event: GridReadyEvent<GridRow>) => setGridApi(event.api);

  /** Клик по ячейке в режиме правки открывает редактор — перенос cellClicked (AppFCFun.kt:100-103). */
  const handleCellClicked = (event: CellClickedEvent<GridRow>) => {
    if (editIcon !== EDIT_YES) return;
    const focused = event.api.getFocusedCell();
    if (focused === null || focused === undefined) return;
    event.api.startEditingCell({
      rowIndex: focused.rowIndex,
      colKey: focused.column.getColId(),
    });
  };

  /** Правка ячейки — перенос valueChanged (AppFCFun.kt:121-134). */
  const handleCellValueChanged = (event: CellValueChangedEvent<GridRow>) => {
    const field = event.colDef.field;
    if (field === undefined || event.data === undefined) return;
    setCellValue(
      event.node.id ?? undefined,
      event.rowIndex ?? 0,
      event.data,
      event.newValue,
      event.oldValue,
      field,
    );
  };

  /**
   * Двойной клик — перенос cellDoubleClicked (AppFCFun.kt:136-142):
   * по первой колонке удаляет строку, по остальным копирует значение в буфер.
   */
  const handleCellDoubleClicked = (event: CellDoubleClickedEvent<GridRow>) => {
    const firstColumn = event.api.getAllDisplayedColumns()[0]?.getColId();
    if (firstColumn !== undefined && firstColumn === event.column.getColId()) {
      deleteSelectedRows();
      return;
    }
    if (editIcon !== EDIT_YES && event.data !== undefined) {
      const value = event.data[event.colDef.field ?? ''];
      void window.navigator.clipboard.writeText(String(value ?? ''));
    }
  };

  /**
   * Выделение строки заполняет карту значений полей — перенос rowSelected (AppFCFun.kt:105-119).
   * Оттуда значения подставляются в {параметры} меню (drill-down).
   *
   * В оригинале проверялось поле node.__selected (прежнее состояние выделения);
   * в ag-grid 36 для этого служит isSelected().
   */
  const handleRowSelected = (event: RowSelectedEvent<GridRow>) => {
    if (event.node?.isSelected() !== true) return;

    const state = useAppStore.getState();
    const rowFieldsValueMap: Record<string, string> = {};

    for (const column of event.api.getAllGridColumns()) {
      const colId = column.getColId();
      const colDef = column.getColDef() as { cellEditor?: string };
      if (colId.startsWith('_') || colDef.cellEditor === 'agLargeTextCellEditor') continue;
      rowFieldsValueMap[colId] = String(event.data?.[colId] ?? '');
    }

    useAppStore.setState({ rowFieldsValueMap });
    void state.refreshMenuItems(false);
  };

  return (
    <AgGridReact<GridRow>
      columnDefs={colList}
      rowData={rowList}
      rowClassRules={ROW_CLASS_RULES}
      rowSelection="multiple"
      headerHeight={30}
      rowHeight={30}
      pagination
      paginationAutoPageSize
      undoRedoCellEditing
      undoRedoCellEditingLimit={20}
      stopEditingWhenCellsLoseFocus
      animateRows
      suppressClickEdit
      onGridReady={handleGridReady}
      onCellClicked={handleCellClicked}
      onCellValueChanged={handleCellValueChanged}
      onCellDoubleClicked={handleCellDoubleClicked}
      onRowSelected={handleRowSelected}
    />
  );
}

export type { GridApi };
